import React from "react";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import useScrollAnimation from "../hooks/useScrollAnimation";

const AudienceSection = () => {
  const [isVisible, sectionRef] = useScrollAnimation(0.2);
  const [chartVisible, chartRef] = useScrollAnimation(0.3);
  
  const surveyData = [
    { label: "C-Suite Executives", percentage: 85, color: "bg-yellow-500" },
    { label: "Mining Engineers", percentage: 75, color: "bg-yellow-400" },
    { label: "Investment Managers", percentage: 65, color: "bg-yellow-300" },
    { label: "Government Officials", percentage: 45, color: "bg-yellow-200" },
    { label: "Industry Analysts", percentage: 70, color: "bg-yellow-600" }
  ];

  return (
    <section id="audiences" className="py-20" style={{ backgroundColor: '#1a2332' }}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-yellow-500 mb-4">
            Our Mining Audience
          </h2>
          <h3 className="text-2xl font-semibold text-white">
            Survey Result
          </h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side - Survey Results */}
          <div>
            <Card ref={chartRef} className="bg-gray-700 border-gray-600" style={{ backgroundColor: '#2d3748' }}>
              <CardContent className="p-8">
                <h4 className="text-xl font-bold text-white mb-8 text-center">
                  Survey Results
                </h4>
                
                <div className="space-y-8">
                  {/* Question 1 */}
                  <div>
                    <p className="text-gray-300 text-sm mb-4">Are you involved in purchasing decisions at your company?</p>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className="text-yellow-500 font-bold text-lg mr-4 w-12">Yes</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-8 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-4" 
                            style={{ 
                              width: chartVisible ? '60%' : '0%',
                              transitionDelay: chartVisible ? '200ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-sm">60%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-yellow-400 font-bold text-lg mr-4 w-12">No</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-400 to-yellow-300 h-6 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-3" 
                            style={{ 
                              width: chartVisible ? '34%' : '0%',
                              transitionDelay: chartVisible ? '400ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-xs">34%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Question 2 */}
                  <div>
                    <p className="text-gray-300 text-sm mb-4">Will your company be making a major capital expenditure in the next six months?</p>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className="text-yellow-500 font-bold text-lg mr-4 w-12">Yes</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-8 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-4" 
                            style={{ 
                              width: chartVisible ? '57%' : '0%',
                              transitionDelay: chartVisible ? '600ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-sm">57%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-yellow-400 font-bold text-lg mr-4 w-12">No</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-400 to-yellow-300 h-6 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-3" 
                            style={{ 
                              width: chartVisible ? '43%' : '0%',
                              transitionDelay: chartVisible ? '800ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-xs">43%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Question 3 */}
                  <div>
                    <p className="text-gray-300 text-sm mb-4">Have you visited a page or gone to a link that appeared on Kitco?</p>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className="text-yellow-500 font-bold text-lg mr-4 w-12">Yes</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-8 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-4" 
                            style={{ 
                              width: chartVisible ? '71%' : '0%',
                              transitionDelay: chartVisible ? '1000ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-sm">71%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-yellow-400 font-bold text-lg mr-4 w-12">No</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-400 to-yellow-300 h-6 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-3" 
                            style={{ 
                              width: chartVisible ? '29%' : '0%',
                              transitionDelay: chartVisible ? '1200ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-xs">29%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Question 4 */}
                  <div>
                    <p className="text-gray-300 text-sm mb-4">Would you recommend Kitco to others?</p>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <span className="text-yellow-500 font-bold text-lg mr-4 w-12">Yes</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-8 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-500 to-yellow-400 h-8 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-4" 
                            style={{ 
                              width: chartVisible ? '91%' : '0%',
                              transitionDelay: chartVisible ? '1400ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-sm">91%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-yellow-400 font-bold text-lg mr-4 w-12">No</span>
                        <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-yellow-400 to-yellow-300 h-6 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-3" 
                            style={{ 
                              width: chartVisible ? '9%' : '0%',
                              transitionDelay: chartVisible ? '1600ms' : '0ms'
                            }}
                          >
                            <span className="text-black font-bold text-xs">9%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Additional Stats */}
          <div className="space-y-6 flex flex-col justify-center">
            <Card className="bg-black border-0" style={{ backgroundColor: '#1a1a1a' }}>
              <CardContent className="p-6 text-center">
                <h3 className="text-4xl font-bold text-yellow-500 mb-3">17M+</h3>
                <p className="text-white text-lg font-semibold mb-1">Yearly Unique Visitors</p>
                <p className="text-gray-500 text-xs mt-2">Source: Google for 2024</p>
                <p className="text-gray-400 text-sm">Leading the mining industry in digital reach</p>
              </CardContent>
            </Card>

            <Card className="bg-black border-0" style={{ backgroundColor: '#1a1a1a' }}>
              <CardContent className="p-6 text-center">
                <h3 className="text-4xl font-bold text-yellow-500 mb-3">170+</h3>
                <p className="text-white text-lg font-semibold mb-1">Countries Reached</p>
                <p className="text-gray-400 text-sm">Global mining community engagement</p>
              </CardContent>
            </Card>

            <Card className="bg-black border-0" style={{ backgroundColor: '#1a1a1a' }}>
              <CardContent className="p-6 text-center">
                <h3 className="text-4xl font-bold text-yellow-500 mb-3">45+</h3>
                <p className="text-white text-lg font-semibold mb-1">Years of Trust</p>
                <p className="text-gray-400 text-sm">Industry authority and credibility</p>
              </CardContent>
            </Card>

            <Card className="bg-black border-0" style={{ backgroundColor: '#1a1a1a' }}>
              <CardContent className="p-6 text-center">
                <h3 className="text-4xl font-bold text-yellow-500 mb-3">90%</h3>
                <p className="text-white text-lg font-semibold mb-1">Customer Satisfaction</p>
                <p className="text-gray-400 text-sm">Based on client feedback and surveys</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Additional Audience Insights */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-white mb-8">Key Audience Demographics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-6 rounded-lg border border-yellow-500/20">
              <div className="text-3xl font-bold text-yellow-500 mb-2">78%</div>
              <div className="text-white font-semibold mb-2">Senior Management</div>
              <div className="text-gray-400 text-sm">Decision makers and influencers</div>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg border border-yellow-500/20">
              <div className="text-3xl font-bold text-yellow-500 mb-2">$100K+</div>
              <div className="text-white font-semibold mb-2">Average Income</div>
              <div className="text-gray-400 text-sm">High-value target audience</div>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-lg border border-yellow-500/20">
              <div className="text-3xl font-bold text-yellow-500 mb-2">25+</div>
              <div className="text-white font-semibold mb-2">Industry Experience</div>
              <div className="text-gray-400 text-sm">Seasoned mining professionals</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;